import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  FileText,
  ClipboardList,
  Clock,
  CheckCircle,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import draftContractService from '../../services/draftContractService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const DraftContract = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalDrafts: 0,
    draftStatus: 0,
    underReviewStatus: 0,
    legalReviewStatus: 0,
    approvedStatus: 0,
    rejectedStatus: 0,
    readyForSignatureStatus: 0,
    totalContractValue: 0,
    pendingReviewValue: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [contractsResponse, statsResponse] = await Promise.all([
        draftContractService.getAllDraftContracts(),
        draftContractService.getDraftContractStats()
      ])
      
      if (contractsResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (contractsResponse.data || []).map(contract => ({
          ...contract,
          id: contract._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (contractsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No draft contract records found. Create your first draft contract!')
        }
      } else {
        toast.error(contractsResponse.message || 'Failed to load draft contracts')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading draft contract records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view draft contracts')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load draft contract records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Draft': 'inactive',
      'Under Review': 'pending',
      'Legal Review': 'pending',
      'Approved': 'active',
      'Rejected': 'danger',
      'Ready for Signature': 'active',
      'Cancelled': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const getPriorityBadgeClass = (priority) => {
    const priorityMap = {
      'Low': 'inactive',
      'Medium': 'pending',
      'High': 'warning',
      'Urgent': 'danger'
    }
    return priorityMap[priority] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'contractNumber',
      header: 'Contract Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'contractTitle',
      header: 'Contract Title',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'partyName',
      header: 'Party',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.partyCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.partyCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'contractType',
      header: 'Type',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'contractValue',
      header: 'Contract Value',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue(), row.original.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getPriorityBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getStatusBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/contracts/draft-contract/form')
  }

  const handleEdit = (contractData) => {
    navigate('/contracts/draft-contract/edit', { state: { contractData } })
  }

  const handleView = (contractData) => {
    navigate('/contracts/draft-contract/view', { state: { contractData } })
  }

  const handleDelete = (contractData) => {
    setDeleteItem(contractData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting draft contract...')
      const response = await draftContractService.deleteDraftContract(deleteItem._id)
      if (response.success) {
        toast.success('Draft contract deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete draft contract')
      }
    } catch (error) {
      console.error('Error deleting draft contract:', error)
      if (error.response?.status === 404) {
        toast.error('Draft contract not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this draft contract')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete draft contract. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Contracts', path: '/contracts' },
          { label: 'Draft Contracts', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Draft Contracts</h1>
          <p>Create and manage contract drafts</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Draft
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <ClipboardList size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalDrafts || 0}</div>
          <div className="metric-title">Total Drafts</div>
          <div className="metric-subtitle">All draft contracts</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <FileText size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Draft</span>
            </div>
          </div>
          <div className="metric-value">{stats.draftStatus || 0}</div>
          <div className="metric-title">Draft Status</div>
          <div className="metric-subtitle">In preparation</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Review</span>
            </div>
          </div>
          <div className="metric-value">{(stats.underReviewStatus || 0) + (stats.legalReviewStatus || 0)}</div>
          <div className="metric-title">Under Review</div>
          <div className="metric-subtitle">Legal or internal review</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Approved</span>
            </div>
          </div>
          <div className="metric-value">{stats.approvedStatus || 0}</div>
          <div className="metric-title">Approved</div>
          <div className="metric-subtitle">Approved drafts</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <FileText size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Ready</span>
            </div>
          </div>
          <div className="metric-value">{stats.readyForSignatureStatus || 0}</div>
          <div className="metric-title">Ready for Signature</div>
          <div className="metric-subtitle">Awaiting execution</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Value</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalContractValue)}</div>
          <div className="metric-title">Total Value</div>
          <div className="metric-subtitle">All drafts combined</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Draft Contract"
        message={`Are you sure you want to delete draft contract "${deleteItem?.contractNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default DraftContract

